import { segmentedPillContainerClasses } from "@ui/components/internal/SegmentedPill";
import { tv } from "@ui/lib/tv";
import { composeRenderProps } from "@ui/primitives/shared";
import {
  SelectionIndicator,
  Tab,
  TabList,
  type TabListProps,
  TabPanel,
  type TabPanelProps,
  type TabProps,
  Tabs,
  type TabsProps,
} from "@ui/primitives/Tabs";
import { createContext, useContext } from "react";

type PillTabSize = "sm" | "lg";

const PillTabSizeContext = createContext<PillTabSize>("sm");

export const pillTabList = tv({
  base: ["inline-flex items-center rounded-full", segmentedPillContainerClasses],
});

export const pillTab = tv({
  base: [
    "relative cursor-pointer rounded-full outline-none whitespace-nowrap",
    "text-neutral-900 selected:font-semibold selected:text-green-700 transition-colors",
    "focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2",
  ],
  defaultVariants: { size: "sm" },
  variants: {
    size: {
      sm: "px-6 py-2 text-base font-normal",
      lg: "px-6 py-2 text-base font-normal lg:px-8 lg:py-3 lg:text-lg",
    },
  },
});

export const pillTabIndicator = tv({
  base: [
    "absolute inset-0 rounded-full border-2 border-green-400 bg-green-50",
    "motion-safe:transition-[translate,width,height]",
  ],
});

export const pillTabPanel = tv({
  base: "outline-none",
});

export interface PillTabsProps extends TabsProps {
  /**
   * - `"sm"` (default) — compact styling.
   * - `"lg"` — larger 60px tabs for page-level navigation.
   */
  size?: PillTabSize;
}

export function PillTabs({ size = "sm", ...props }: PillTabsProps) {
  return (
    <PillTabSizeContext.Provider value={size}>
      <Tabs {...props} />
    </PillTabSizeContext.Provider>
  );
}

export interface PillTabListProps<T extends object> extends TabListProps<T> {}

export function PillTabList<T extends object>(props: PillTabListProps<T>) {
  return (
    <TabList {...props} className={composeRenderProps(props.className, (className) => pillTabList({ className }))} />
  );
}

export interface PillTabProps extends TabProps {}

export function PillTab(props: PillTabProps) {
  const size = useContext(PillTabSizeContext);
  return (
    <Tab
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        pillTab({ ...renderProps, size, className }),
      )}
    >
      {composeRenderProps(props.children, (children) => (
        <>
          <span className="relative z-10">{children}</span>
          <SelectionIndicator className={pillTabIndicator()} />
        </>
      ))}
    </Tab>
  );
}

export interface PillTabPanelProps extends TabPanelProps {}

export function PillTabPanel(props: PillTabPanelProps) {
  return (
    <TabPanel {...props} className={composeRenderProps(props.className, (className) => pillTabPanel({ className }))} />
  );
}
